import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  getAuth,
  updateProfile
} from 'firebase/auth';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';
import firebaseConfig from '../../firebase/config';
import { isCentralRole } from './authSlice';

const DEFAULT_ORGANIZATION_ID = process.env.REACT_APP_DEFAULT_ORGANIZATION_ID || 'default_organization';
const SECONDARY_APP_NAME = 'operator-sub-account';
const CENTRAL_ACCOUNT_ROLES = ['admin', 'dispatcher', 'central_admin', 'central_dispatch'];

const getSecondaryAuth = () => {
  const app = getApps().some(existingApp => existingApp.name === SECONDARY_APP_NAME)
    ? getApp(SECONDARY_APP_NAME)
    : initializeApp(firebaseConfig, SECONDARY_APP_NAME);

  return getAuth(app);
};

const normalizeOperator = (operatorDoc) => ({
  id: operatorDoc.id,
  ...operatorDoc.data()
});

export const fetchOperators = createAsyncThunk(
  'operators/fetchOperators',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!currentUser) throw new Error('Nicht angemeldet');

      if (isCentralRole(currentUser)) {
        const operatorsQuery = query(collection(db, 'operators'), orderBy('name', 'asc'));
        const snapshot = await getDocs(operatorsQuery);
        return snapshot.docs.map(normalizeOperator);
      } else if (currentUser.operatorId) {
        const operatorDoc = await getDoc(doc(db, 'operators', currentUser.operatorId));
        return operatorDoc.exists() ? [normalizeOperator(operatorDoc)] : [];
      } else {
        return [];
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOperatorUsers = createAsyncThunk(
  'operators/fetchOperatorUsers',
  async (operatorId, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser) && currentUser?.operatorId !== operatorId) {
        throw new Error('Keine Berechtigung fuer diese Sub-Konten');
      }

      const usersQuery = query(
        collection(db, 'users'),
        where('operatorId', '==', operatorId)
      );
      const snapshot = await getDocs(usersQuery);
      return {
        operatorId,
        users: snapshot.docs
          .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
          // hiddenFromOperatorsPage blendet technische/Support-Konten aus.
          .filter(user => (user.role === 'operator_admin' || user.role === 'operator_staff') && user.hiddenFromOperatorsPage !== true)
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCentralAccounts = createAsyncThunk(
  'operators/fetchCentralAccounts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins koennen Hauptzentralen-Konten laden');
      }

      const usersQuery = query(
        collection(db, 'users'),
        where('role', 'in', CENTRAL_ACCOUNT_ROLES)
      );
      const snapshot = await getDocs(usersQuery);
      return snapshot.docs
        .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
        // hiddenFromOperatorsPage: optionales Boolean im users-Dokument, um
        // technische/Support-Admins aus „Unternehmer & Sub-Konten" auszublenden.
        .filter(user => user.isDriver !== true && !user.operatorId && user.hiddenFromOperatorsPage !== true)
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fahrzeuglimit robust normalisieren: nur nicht-negative Ganzzahlen zulassen
// (Dezimal-/Negativwerte oder ungültige Eingaben werden auf 0 abgefangen).
const sanitizeVehicleLimit = (value) => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

// Auszahlungs-Referenzdaten (Konzept 8.7): wie der Sub-Unternehmer seinen
// Anteil aus der Auszahlungsübersicht erhalten möchte. Steuern keine
// Live-Zahlung; nur normalisiert ablegen, nicht-relevante Felder leeren.
const buildPayoutFields = (operatorData) => {
  const method = ['paypal', 'bank'].includes(operatorData.payoutMethod)
    ? operatorData.payoutMethod
    : 'none';
  return {
    payoutMethod: method,
    payoutPaypalEmail: method === 'paypal' ? (operatorData.payoutPaypalEmail || '').trim().toLowerCase() : '',
    payoutIban: method === 'bank' ? (operatorData.payoutIban || '').replace(/\s+/g, '').toUpperCase() : '',
    payoutBic: method === 'bank' ? (operatorData.payoutBic || '').replace(/\s+/g, '').toUpperCase() : '',
    payoutAccountHolder: method === 'bank' ? (operatorData.payoutAccountHolder || '').trim() : ''
  };
};

export const createOperator = createAsyncThunk(
  'operators/createOperator',
  async (operatorData, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins koennen Unternehmer anlegen');
      }

      const payload = {
        organizationId: currentUser.organizationId || DEFAULT_ORGANIZATION_ID,
        name: operatorData.name.trim(),
        legalName: (operatorData.legalName || '').trim(),
        contactName: (operatorData.contactName || '').trim(),
        email: (operatorData.email || '').trim(),
        phone: (operatorData.phone || '').trim(),
        address: (operatorData.address || '').trim(),
        vehicleLimit: sanitizeVehicleLimit(operatorData.vehicleLimit),
        // Quittungs-Stammdaten (§ 33 UStDV): leistender Unternehmer je Fahrt.
        taxNumber: (operatorData.taxNumber || '').trim(),
        vatId: (operatorData.vatId || '').trim(),
        notes: (operatorData.notes || '').trim(),
        active: operatorData.active !== false,
        ...buildPayoutFields(operatorData),
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      };

      const docRef = await addDoc(collection(db, 'operators'), payload);
      return { id: docRef.id, ...payload };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateOperator = createAsyncThunk(
  'operators/updateOperator',
  async ({ id, ...operatorData }, { getState, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins koennen Unternehmer bearbeiten');
      }

      const updateData = {
        name: operatorData.name.trim(),
        legalName: (operatorData.legalName || '').trim(),
        contactName: (operatorData.contactName || '').trim(),
        email: (operatorData.email || '').trim(),
        phone: (operatorData.phone || '').trim(),
        address: (operatorData.address || '').trim(),
        vehicleLimit: sanitizeVehicleLimit(operatorData.vehicleLimit),
        // Quittungs-Stammdaten (§ 33 UStDV): leistender Unternehmer je Fahrt.
        taxNumber: (operatorData.taxNumber || '').trim(),
        vatId: (operatorData.vatId || '').trim(),
        notes: (operatorData.notes || '').trim(),
        active: operatorData.active !== false,
        ...buildPayoutFields(operatorData),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      };

      await updateDoc(doc(db, 'operators', id), updateData);
      return { id, ...updateData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCentralAccount = createAsyncThunk(
  'operators/createCentralAccount',
  async ({ email, password, name, phone, role = 'dispatcher' }, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins koennen Hauptzentralen-Konten anlegen');
      }
      if (!['admin', 'dispatcher'].includes(role)) {
        throw new Error('Ungueltige Hauptzentralen-Rolle');
      }
      if (!password || password.length < 8) {
        throw new Error('Passwort muss mindestens 8 Zeichen lang sein');
      }

      const secondaryAuth = getSecondaryAuth();
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), password);
      const createdUser = userCredential.user;

      await updateProfile(createdUser, { displayName: name.trim() });
      await secondaryAuth.signOut();

      const userData = {
        uid: createdUser.uid,
        email: email.trim(),
        name: name.trim(),
        phone: (phone || '').trim(),
        role,
        organizationId: currentUser.organizationId || DEFAULT_ORGANIZATION_ID,
        operatorId: null,
        operatorIds: [],
        isDriver: false,
        isAdmin: role === 'admin',
        active: true,
        permissions: [],
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        profile_picture: ''
      };

      await setDoc(doc(db, 'users', createdUser.uid), userData);
      dispatch(fetchCentralAccounts());
      return { id: createdUser.uid, ...userData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createOperatorSubAccount = createAsyncThunk(
  'operators/createOperatorSubAccount',
  async ({ operatorId, email, password, name, phone, role = 'operator_admin' }, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins koennen Sub-Konten anlegen');
      }
      if (!operatorId) throw new Error('Kein Unternehmer ausgewaehlt');
      if (!['operator_admin', 'operator_staff'].includes(role)) {
        throw new Error('Ungueltige Sub-Konto-Rolle');
      }
      if (!password || password.length < 8) {
        throw new Error('Passwort muss mindestens 8 Zeichen lang sein');
      }

      const secondaryAuth = getSecondaryAuth();
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), password);
      const createdUser = userCredential.user;

      await updateProfile(createdUser, { displayName: name.trim() });
      await secondaryAuth.signOut();

      const userData = {
        uid: createdUser.uid,
        email: email.trim(),
        name: name.trim(),
        phone: (phone || '').trim(),
        role,
        organizationId: currentUser.organizationId || DEFAULT_ORGANIZATION_ID,
        operatorId,
        operatorIds: [operatorId],
        isDriver: false,
        isAdmin: false,
        active: true,
        permissions: [],
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        profile_picture: ''
      };

      await setDoc(doc(db, 'users', createdUser.uid), userData);
      dispatch(fetchOperatorUsers(operatorId));
      return { id: createdUser.uid, ...userData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const setCentralAccountActive = createAsyncThunk(
  'operators/setCentralAccountActive',
  async ({ userId, active }, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins koennen Hauptzentralen-Konten bearbeiten');
      }
      if (currentUser.uid === userId && active === false) {
        throw new Error('Das eigene Konto kann hier nicht deaktiviert werden');
      }

      await updateDoc(doc(db, 'users', userId), {
        active,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      dispatch(fetchCentralAccounts());
      return { userId, active };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Setzt das Passwort eines Unternehmer-Portalzugangs (operator_admin /
// operator_staff) über die Cloud Function setOperatorAccountPassword (Admin
// SDK; nur zentrale Rollen). Das Passwort wird NICHT in Firestore gespeichert.
export const resetSubAccountPassword = createAsyncThunk(
  'operators/resetSubAccountPassword',
  async ({ userId, newPassword }, { rejectWithValue }) => {
    try {
      const setOperatorAccountPasswordFn = httpsCallable(functions, 'setOperatorAccountPassword');
      await setOperatorAccountPasswordFn({ userId, newPassword });
      return { userId };
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Unternehmer-Passworts:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const setOperatorSubAccountActive = createAsyncThunk(
  'operators/setOperatorSubAccountActive',
  async ({ userId, operatorId, active }, { getState, dispatch, rejectWithValue }) => {
    try {
      const currentUser = getState().auth.user;
      if (!isCentralRole(currentUser)) {
        throw new Error('Nur zentrale Admins koennen Sub-Konten bearbeiten');
      }

      await updateDoc(doc(db, 'users', userId), {
        active,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      dispatch(fetchOperatorUsers(operatorId));
      return { userId, operatorId, active };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const operatorsSlice = createSlice({
  name: 'operators',
  initialState: {
    operators: [],
    usersByOperatorId: {},
    centralAccounts: [],
    isLoading: false,
    isSaving: false,
    error: null
  },
  reducers: {
    clearOperatorsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOperators.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOperators.fulfilled, (state, action) => {
        state.isLoading = false;
        state.operators = action.payload;
      })
      .addCase(fetchOperators.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchOperatorUsers.fulfilled, (state, action) => {
        state.usersByOperatorId[action.payload.operatorId] = action.payload.users;
      })
      .addCase(fetchCentralAccounts.fulfilled, (state, action) => {
        state.centralAccounts = action.payload;
      })
      .addMatcher(
        (action) => [
          createOperator.pending.type,
          updateOperator.pending.type,
          createCentralAccount.pending.type,
          createOperatorSubAccount.pending.type,
          setCentralAccountActive.pending.type,
          setOperatorSubAccountActive.pending.type
        ].includes(action.type),
        (state) => {
          state.isSaving = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => [
          createOperator.fulfilled.type,
          updateOperator.fulfilled.type,
          createCentralAccount.fulfilled.type,
          createOperatorSubAccount.fulfilled.type,
          setCentralAccountActive.fulfilled.type,
          setOperatorSubAccountActive.fulfilled.type
        ].includes(action.type),
        (state) => {
          state.isSaving = false;
        }
      )
      .addMatcher(
        (action) => [
          createOperator.rejected.type,
          updateOperator.rejected.type,
          createCentralAccount.rejected.type,
          createOperatorSubAccount.rejected.type,
          setCentralAccountActive.rejected.type,
          setOperatorSubAccountActive.rejected.type
        ].includes(action.type),
        (state, action) => {
          state.isSaving = false;
          state.error = action.payload;
        }
      );
  }
});

export const { clearOperatorsError } = operatorsSlice.actions;
export default operatorsSlice.reducer;
